import { PencilSquareIcon } from "@heroicons/react/24/solid";
import { useState, useRef, useEffect } from "react";
import { useQueryClient } from "react-query";
import { useHistory } from "react-router-dom";
import { useRecoilState, useRecoilValue } from "recoil";
import ButtonForModal from "../../../../components/ButtonForModal";
import ConfirmBoxDelete from "../../../../components/ConfirmBoxDelete";
import MaterialExTable from "../../../../components/tables/MaterialExTable";
import { toast } from "../../../../components/toastNotification";
import { permissionsSelector } from "../../../Login/selectors/permissionSelector";
import { PERMISSIONS } from "../../../PermissionsConstants";
import { VendorTabs } from "../../../VendorTab/VendorTab";
import { mainSelectedTab } from "../../atoms";
import { selectedRowsLE } from "../../LeasedEquipment/atom/atom";
import { useDeleteLE } from "../../LeasedEquipment/hooks/useDeleteLeasedEquipment";

interface LeasedEquipmentTableProps {
  data;
}

export const VendorLeasedEquipmentTable: React.FC<LeasedEquipmentTableProps> =
  ({ data }) => {
    const {
      mutate: BulkDeleteLeasedEquipments,
      isSuccess: deacSuccess,
      data: deacResult,
      reset: resetDeacData,
    } = useDeleteLE();
    const [openModalDelete, setOpenModalDelete] = useState(false);
    const [selectedRows, setSelectedRows] = useRecoilState(selectedRowsLE);
    const [isBulkSelected, setIsBulkSelected] = useState(false);
    const queryClient = useQueryClient();

    const history = useHistory();
    const [, setSelectedIndex] = useRecoilState(mainSelectedTab);

    const userPermissions = useRecoilValue(permissionsSelector);
    const editPermission = PERMISSIONS.permission_leasedequipment_edit;
    const deletePermission = PERMISSIONS.permission_leasedequipment_delete;
    const tableRef = useRef<any>([]);

    const handleLinkLeasedEdit = (rowData) => {
      const url = `/vendortab/vendor`;
      history.push({
        pathname: `/officesettings/leasedequipment`,
        state: {
          action: "fromVendorLEEdit",
          vendorId: rowData.vendorId,
          rowDetails: rowData,
        },
      });
      window.location.reload();
      let userTabs = [
        {
          to: "",
        },
      ];

      [...VendorTabs].forEach((tabs) => {
        let temp = [...userPermissions].filter(
          (user) => user === tabs.permission
        );
        if (temp.length > 0)
          userTabs.push({
            to: tabs.to,
          });
      });

      userTabs.shift();
      const index = userTabs.findIndex((c) => c.to === url);
      setSelectedIndex(index);
    };

    const handleLink = (rowData) => {
      const url = `/vendortab/vendor`;
      history.push({
        pathname: `/officesettings/leasedequipment`,
        state: {
          action: "fromVendorViewLE",
          vendorData: rowData,
        },
      });
      window.location.reload();
      let userTabs = [
        {
          to: "",
        },
      ];

      [...VendorTabs].forEach((tabs) => {
        let temp = [...userPermissions].filter(
          (user) => user === tabs.permission
        );
        if (temp.length > 0)
          userTabs.push({
            to: tabs.to,
          });
      });

      userTabs.shift();
      const index = userTabs.findIndex((c) => c.to === url);
      setSelectedIndex(index);
    };

    const handleSelectedRowsChange = (rows) => {
      const forDelete = rows
        .filter((x) => x.isActive)
        .map((r) => {
          const { tableData, ...rows } = r;
          return rows;
        });

      setSelectedRows(forDelete);
      setIsBulkSelected((prev) => !prev);
    };

    const handleBulkDelete = () => {
      const ids = selectedRows
        ? selectedRows.map((row) => row.recordNumber)
        : undefined;
      BulkDeleteLeasedEquipments(ids);
      setSelectedRows([]);
      setOpenModalDelete(false);
      setIsBulkSelected(false);
    };

    if (deacSuccess && deacResult) {
      if (deacResult.status === 201 || deacResult.status === 200) {
        toast.fire(
          "Success",
          "Leased Equipment were deactivated successfully",
          "success"
        );
        queryClient.invalidateQueries(["GetVendorLeasedEquipment"]);
        setSelectedRows([]);
      } else if (deacResult.status === 500) {
        if (
          deacResult.data.message ===
            "Service not available, closing transmission channel. The server response was: 4.3.2 Service not available" ||
          deacResult.data.message ===
            "Service not available, closing transmission channel. The server response was: 4.3.2 Service not available"
        ) {
          toast.fire(
            "Warning",
            "Leased Equipment were successfully deactivated, but " +
              deacResult.data.message,
            "warning"
          );
          setSelectedRows([]);
        } else {
          toast.fire(
            "Bulk deactivation of Leased Equipment failed",
            "Server Error: Cannot deactivate Leased Equipment",
            "error"
          );
        }

        queryClient.invalidateQueries(["GetVendorLeasedEquipment"]);
      } else {
        toast.fire(
          "Bulk deactivation of Leased Equipment failed",
          "Server Error: Cannot deactivate Leased Equipment",
          "error"
        );
        queryClient.invalidateQueries(["GetVendorLeasedEquipment"]);
      }
      resetDeacData();
    }

    const deleteMessage = (
      <div className="text-gray-500 font-normal">{`Are you sure you want to deactivate ${
        selectedRows?.length
      } ${
        selectedRows
          ? selectedRows.length > 1
            ? "Leased Equipments"
            : "Leased Equipment"
          : undefined
      }?`}</div>
    );

    const alignCenter = "center" as
      | "center"
      | "inherit"
      | "justify"
      | "left"
      | "right"
      | undefined;

    const matColumns = [
      {
        title: "Record #",
        field: "recordNumber",
        render: (rowdata) => {
          return (
            <div className="cursor-pointer" onClick={() => handleLink(rowdata)}>
              <span className="blueAndBold underline">
                {"LE-" + String(rowdata.recordNumber)?.padStart(4, "0")}
              </span>
              <br></br>
            </div>
          );
        },
        customFilterAndSearch: (term, rowData) =>
          ("LE-" + String(rowData.recordNumber)?.padStart(4, "0"))
            .toLowerCase()
            .indexOf(term.toLowerCase()) !== -1,
      },
      {
        title: "Type",
        field: "typeName",
      },
      {
        title: "Make",
        field: "makeName",
      },
      {
        title: "Model/Version",
        field: "modelVersion",
        render: (rowDetails) => {
          return (
            <div className="flex items-center space-x-1 whitespace-normal ">
              {rowDetails?.modelDescription
                ? rowDetails?.model + " (" + rowDetails?.modelDescription + ")"
                : rowDetails?.model}
            </div>
          );
        },
      },
      {
        title: "Equipment ID/Serial Number",
        field: "serialNumber",
        render: (rowDetails) => {
          return (
            <div className="flex items-center space-x-1 whitespace-normal ">
              {rowDetails.serialNumber}
            </div>
          );
        },
      },
      {
        title: "IP Address",
        field: "ipaddress",
      },
      {
        title: "Lease End Date",
        field: "leLeasedEndDate",
        render: (rowData) => {
          return <div>{rowData.leLeasedEndDate}</div>;
        },
      },
      {
        title: "Servicing Dealer",
        field: "name",
      },
      {
        title: "Status",
        field: "status",

        render: (rowdata) => {
          return <div> {rowdata.status}</div>;
        },
      },

      {
        title: "Action",
        align: alignCenter,
        render: (rowData) => {
          return userPermissions.includes(editPermission) ? (
            <div className="flex place-content-center mr-4">
              <ButtonForModal
                className={
                  rowData?.status?.includes("Closed")
                    ? "border border-quaternary text-white bg-quaternary opacity-50"
                    : "border border-quaternary text-white bg-quaternary"
                }
                onClick={() => {
                  handleLinkLeasedEdit(rowData);
                }}
                isDisabled={false}
              >
                <PencilSquareIcon className="h-4 w-4" />
                Edit
              </ButtonForModal>
            </div>
          ) : (
            ""
          );
        },
      },
    ];

    return (
      <>
        <div className="w-full p-2 bg-white">
          <div className="rounded-lg border-primary box-shadow">
            <div className="overflow-x-auto grid grid-row">
              <MaterialExTable
                columns={matColumns}
                data={data}
                isLoading={false}
                search={true}
                selectedRows={selectedRows}
                selection={userPermissions.includes(deletePermission)}
                toolbar={true}
                setOpenModal={setOpenModalDelete}
                onSelectionChange={handleSelectedRowsChange}
                actions={undefined}
                editable={undefined}
                exportButton={undefined}
                grouping={undefined}
                detailPanel={undefined}
                toolBarComponent={undefined}
                panelWidth={undefined}
                tableRef={tableRef}
                onSearchChange={undefined}
                fileName={undefined}
                title={undefined}
              />
            </div>
          </div>
        </div>
        <ConfirmBoxDelete
          ModalTitle="Deactivate Leased Equipment"
          qConfirmation={deleteMessage}
          isOpen={openModalDelete}
          onClose={() => setOpenModalDelete(false)}
          onClickFunction={isBulkSelected ? handleBulkDelete : handleBulkDelete}
          cancelButtonRef={undefined}
        />
      </>
    );
  };
